import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Partner {
  id: string;
  name: string;
  description: string | null;
  website_url: string | null;
  is_active: boolean;
}

interface PartnerSlot {
  id: string;
  milestone_days: number;
  partner_id: string;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  tier: string | null;
  partner: Partner;
}

export interface PartnerSlotWithPartner {
  slot: PartnerSlot;
  partner: Partner;
}

export function usePartnerSlots() {
  const { user, group } = useAuth();

  return useQuery({
    queryKey: ['partner-slots', group?.id],
    queryFn: async (): Promise<Map<number, PartnerSlotWithPartner>> => {
      // Use service role via edge function or RPC to fetch partner slots
      // Since partner_slots has restrictive RLS, we need to use a public read approach
      // For now, we'll create a simple fetch that handles errors gracefully
      
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch active partner slots with their partners
      // This requires the tables to have appropriate RLS or we use an RPC
      const { data: slots, error } = await supabase
        .from('partner_slots')
        .select(`
          id,
          milestone_days,
          partner_id,
          is_active,
          start_date,
          end_date,
          tier,
          partners (
            id,
            name,
            description,
            website_url,
            is_active
          )
        `)
        .eq('is_active', true)
        .or(`start_date.is.null,start_date.lte.${today}`)
        .or(`end_date.is.null,end_date.gte.${today}`);

      if (error) {
        // Silently fail - no partner benefits shown
        console.error('Failed to fetch partner slots:', error);
        return new Map();
      }

      if (!slots) return new Map();

      const slotMap = new Map<number, PartnerSlotWithPartner>();
      
      for (const slot of slots) {
        const partner = slot.partners as unknown as Partner;
        
        // Skip if partner is not active
        if (!partner || !partner.is_active) continue;
        
        // Only one partner per milestone (first found wins)
        if (!slotMap.has(slot.milestone_days)) {
          slotMap.set(slot.milestone_days, {
            slot: { ...slot, partner },
            partner,
          });
        }
      }

      return slotMap;
    },
    enabled: !!user && !!group,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: false, // Don't retry on failure - just hide partner content
  });
}
